import { useState, useEffect, useRef, useMemo } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import PWAInstallPrompt from "../components/PWAInstallPrompt";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Clock,
  LogOut,
  Menu,
  X,
  MapPin,
  Camera,
  Home,
  FileText,
  User,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Loader2,
  WifiOff,
  Wifi,
  CloudOff,
  RefreshCw,
  Sun,
  Download,
  AlertCircle,
  Calendar,
  FileDown
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Função para formatar horas decimais em "Xh Ymin"
function formatHoursMinutes(decimalHours, showSign = false) {
  if (decimalHours === null || decimalHours === undefined || isNaN(decimalHours)) {
    return "0h 00min";
  }
  
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  
  // Ajustar caso os minutos arredondem para 60
  let finalHours = hours;
  let finalMinutes = minutes;
  if (finalMinutes === 60) {
    finalHours += 1;
    finalMinutes = 0;
  }
  
  const sign = showSign ? (isNegative ? "-" : "+") : (isNegative ? "-" : "");
  return `${sign}${finalHours}h ${String(finalMinutes).padStart(2, '0')}min`;
}

// Funções para gerenciar registros offline
const OFFLINE_RECORDS_KEY = "control_isp_offline_records";

const getOfflineRecords = () => {
  try {
    const records = localStorage.getItem(OFFLINE_RECORDS_KEY);
    return records ? JSON.parse(records) : [];
  } catch {
    return [];
  }
};

const saveOfflineRecord = (record) => {
  const records = getOfflineRecords();
  records.push({ ...record, offline_id: Date.now(), synced: false });
  localStorage.setItem(OFFLINE_RECORDS_KEY, JSON.stringify(records));
};

const removeOfflineRecord = (offlineId) => {
  const records = getOfflineRecords();
  const updated = records.filter(r => r.offline_id !== offlineId);
  localStorage.setItem(OFFLINE_RECORDS_KEY, JSON.stringify(updated));
};

const clearSyncedRecords = () => {
  const records = getOfflineRecords();
  const pending = records.filter(r => !r.synced);
  localStorage.setItem(OFFLINE_RECORDS_KEY, JSON.stringify(pending));
};

// Clock In/Out Component
function ClockInOut() {
  const [loading, setLoading] = useState(false);
  const [todayRecords, setTodayRecords] = useState([]);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [pontoLiberado, setPontoLiberado] = useState(true); // Assume liberado até verificar
  const [checkingPermission, setCheckingPermission] = useState(true);
  
  // Camera states
  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pendingRecordType, setPendingRecordType] = useState(null);
  const [facingMode, setFacingMode] = useState("user"); // "user" = frontal, "environment" = traseira
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Face verification states
  const [verifyingFace, setVerifyingFace] = useState(false);
  const [faceVerificationResult, setFaceVerificationResult] = useState(null);
  const [faceVerificationEnabled, setFaceVerificationEnabled] = useState(false);
  
  // Offline states
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingOfflineRecords, setPendingOfflineRecords] = useState([]);
  const [syncing, setSyncing] = useState(false);
  
  // Feriados ativos
  const [feriadosAtivos, setFeriadosAtivos] = useState([]);
  
  // Ajustes manuais de banco de horas
  const [manualBankHoursAdjustment, setManualBankHoursAdjustment] = useState(0);

  // Buscar ajustes de banco de horas do funcionário
  useEffect(() => {
    const fetchBankHoursAdjustments = async () => {
      try {
        const response = await axios.get(`${API}/employees/me/bank-hours`);
        setManualBankHoursAdjustment(response.data.manual_adjustment_balance || 0);
      } catch (error) {
        console.log("Erro ao buscar ajustes de banco de horas:", error);
        setManualBankHoursAdjustment(0);
      }
    };
    
    fetchBankHoursAdjustments();
  }, []);

  // Buscar feriados ativos da empresa
  useEffect(() => {
    const fetchFeriados = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const response = await axios.get(`${API}/holidays/company?year=${currentYear}`);
        const ativos = (response.data.feriados || [])
          .filter(f => f.ativo)
          .map(f => f.data); // Retorna array de datas no formato "YYYY-MM-DD"
        setFeriadosAtivos(ativos);
        console.log("Feriados ativos:", ativos);
      } catch (error) {
        console.log("Erro ao buscar feriados:", error);
      }
    };
    
    fetchFeriados();
  }, []);

  // Verificar se o ponto está liberado e se verificação facial está ativada
  useEffect(() => {
    const checkPontoPermission = async () => {
      try {
        const response = await axios.get(`${API}/employees/me/schedule`);
        console.log("Dados do funcionário:", response.data);
        console.log("ponto_liberado:", response.data?.ponto_liberado);
        // Verificar explicitamente se é true
        const liberado = response.data?.ponto_liberado === true;
        console.log("Ponto liberado?", liberado);
        setPontoLiberado(liberado);
        
        // Verificar se a empresa tem verificação facial ativada
        if (response.data?.company_face_verification_enabled) {
          setFaceVerificationEnabled(true);
          console.log("Verificação facial ATIVADA para esta empresa");
        }
      } catch (error) {
        console.log("Não foi possível verificar permissão de ponto:", error);
        setPontoLiberado(false); // Em caso de erro, assume bloqueado por segurança
      } finally {
        setCheckingPermission(false);
      }
    };
    
    if (navigator.onLine) {
      checkPontoPermission();
    } else {
      setCheckingPermission(false);
      // Offline: tentar carregar do localStorage
      const cachedData = localStorage.getItem("employee_ponto_liberado");
      if (cachedData !== null) {
        setPontoLiberado(cachedData === "true");
      }
    }
  }, []);

  // Função para atualizar lista de pendentes
  const updatePendingRecords = () => {
    const records = getOfflineRecords().filter(r => !r.synced && !r.error);
    setPendingOfflineRecords(records);
  };

  // Sync offline records when online
  const syncOfflineRecords = async () => {
    const records = getOfflineRecords().filter(r => !r.synced && !r.error);
    if (records.length === 0) {
      toast.info("Nenhum registro pendente para sincronizar");
      return;
    }
    
    setSyncing(true);
    let syncedCount = 0;
    let failedCount = 0;
    let errors = [];
    
    for (const record of records) {
      try {
        const response = await axios.post(`${API}/time-records/sync`, {
          record_type: record.record_type,
          timestamp: record.timestamp,
          latitude: record.latitude,
          longitude: record.longitude,
          photo_url: record.photo_url,
          notes: record.notes || "Registrado offline",
          offline_id: record.offline_id,
          registered_offline: true
        });
        
        if (response.data) {
          removeOfflineRecord(record.offline_id);
          syncedCount++;
        }
      } catch (error) {
        console.error("Erro ao sincronizar registro:", error);
        failedCount++;
        
        // Capturar mensagem de erro
        const errorMsg = error.response?.data?.detail || error.message;
        errors.push(errorMsg);
        
        // Se o registro foi rejeitado (403), marcar como erro permanente
        if (error.response?.status === 403) {
          const allRecords = getOfflineRecords();
          const updated = allRecords.map(r => 
            r.offline_id === record.offline_id 
              ? { ...r, synced: true, error: errorMsg }
              : r
          );
          localStorage.setItem(OFFLINE_RECORDS_KEY, JSON.stringify(updated));
        }
      }
    }
    
    // Atualizar lista de pendentes
    updatePendingRecords();
    
    // Mostrar resultado
    if (syncedCount > 0 && failedCount === 0) {
      toast.success(`✅ ${syncedCount} registro(s) sincronizado(s) com sucesso!`);
      fetchTodayRecords();
    } else if (syncedCount > 0 && failedCount > 0) {
      toast.warning(`⚠️ ${syncedCount} sincronizado(s), ${failedCount} com erro`);
      fetchTodayRecords();
    } else if (failedCount > 0) {
      toast.error(`❌ Falha ao sincronizar: ${errors[0] || "Erro desconhecido"}`);
    }
    
    setSyncing(false);
  };

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("🌐 Conexão restabelecida!");
      // Sincronizar após um pequeno delay para garantir que a conexão está estável
      setTimeout(() => {
        syncOfflineRecords();
      }, 1000);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("📴 Você está offline. Os registros serão salvos localmente.");
    };
    
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    
    // Load pending offline records
    updatePendingRecords();
    
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchTodayRecords();
    getLocation();
    
    // Try to sync on mount if online
    if (navigator.onLine) {
      syncOfflineRecords();
    }
    
    // Cleanup camera on unmount
    return () => {
      stopCamera();
    };
  }, []);

  const fetchTodayRecords = async () => {
    try {
      const response = await axios.get(`${API}/time-records/today`);
      setTodayRecords(response.data);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  const getLocation = () => {
    setGettingLocation(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setGettingLocation(false);
        },
        (error) => {
          setLocationError("Não foi possível obter sua localização");
          setGettingLocation(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      setLocationError("Geolocalização não suportada");
      setGettingLocation(false);
    }
  };

  // Camera functions
  const [flashEnabled, setFlashEnabled] = useState(false);

  const startCamera = async (recordType, cameraMode = facingMode) => {
    setPendingRecordType(recordType);
    setShowCamera(true);
    setCapturedPhoto(null);
    
    // Parar câmera anterior se existir
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    try {
      // Usar a câmera selecionada (frontal por padrão)
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: cameraMode,
          width: { ideal: 640 }, 
          height: { ideal: 480 }
        } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      // Tentar ativar o flash/lanterna se for câmera traseira
      if (cameraMode === "environment") {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities?.();
        if (capabilities?.torch) {
          try {
            await videoTrack.applyConstraints({ advanced: [{ torch: true }] });
            setFlashEnabled(true);
          } catch {
            setFlashEnabled(false);
          }
        }
      } else {
        setFlashEnabled(false);
      }
    } catch (error) {
      console.error("Erro ao acessar câmera:", error);
      toast.error("Não foi possível acessar a câmera. Verifique as permissões.");
      setShowCamera(false);
    }
  };

  // Função para alternar entre câmera frontal e traseira
  const toggleCamera = async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    
    // Reiniciar a câmera com o novo modo
    if (showCamera && pendingRecordType) {
      await startCamera(pendingRecordType, newMode);
      toast.info(newMode === "user" ? "📷 Câmera frontal" : "📷 Câmera traseira");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      // Desligar flash antes de parar a câmera
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        try {
          videoTrack.applyConstraints({ advanced: [{ torch: false }] });
        } catch (e) {
          // Flash não suportado, ignorar
          console.log("Flash não disponível:", e);
        }
      }
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setFlashEnabled(false);
  };

  // Função para calcular o brilho médio da imagem
  const calculateBrightness = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    let totalBrightness = 0;
    const pixelCount = data.length / 4;
    
    for (let i = 0; i < data.length; i += 4) {
      // Calcular luminância usando fórmula padrão
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
      totalBrightness += brightness;
    }
    
    return totalBrightness / pixelCount; // Média de 0-255
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      
      // Se for câmera frontal, desinverter a imagem para ficar correta
      if (facingMode === "user") {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      
      ctx.drawImage(video, 0, 0);
      
      // Resetar transformações
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      
      // Verificar brilho da imagem
      const brightness = calculateBrightness(canvas);
      const MIN_BRIGHTNESS = 40; // Mínimo de brilho aceitável (0-255)
      
      if (brightness < MIN_BRIGHTNESS) {
        toast.error(
          `📸 Foto muito escura! Procure um local com mais iluminação.`,
          { duration: 5000 }
        );
        return; // Não aceita a foto
      }
      
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photoData);
      stopCamera();
      setFlashEnabled(false);
      
      // Feedback sobre qualidade
      if (brightness < 70) {
        toast.warning("⚠️ Foto com pouca luz, mas aceitável.");
      } else {
        toast.success("✅ Foto capturada!");
      }
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera(pendingRecordType);
  };

  const cancelCamera = () => {
    stopCamera();
    setShowCamera(false);
    setCapturedPhoto(null);
    setPendingRecordType(null);
  };

  const uploadPhotoAndRegister = async () => {
    if (!capturedPhoto || !pendingRecordType) return;
    
    setUploadingPhoto(true);
    setFaceVerificationResult(null);
    
    try {
      // Se a verificação facial está ativada, verificar primeiro
      if (faceVerificationEnabled && isOnline) {
        setVerifyingFace(true);
        toast.loading("🔍 Verificando identidade facial...", { id: "face-verify" });
        
        try {
          // Buscar ID do funcionário
          const meResponse = await axios.get(`${API}/employees/me`);
          const employeeId = meResponse.data.id;
          
          // Enviar para verificação facial
          const verifyResponse = await axios.post(`${API}/verify-face`, {
            employee_id: employeeId,
            photo_base64: capturedPhoto
          });
          
          const result = verifyResponse.data;
          setFaceVerificationResult(result);
          
          if (!result.verified) {
            toast.dismiss("face-verify");
            toast.error(
              <div className="flex flex-col gap-1">
                <span className="font-bold">❌ Verificação facial falhou!</span>
                <span className="text-sm">{result.message}</span>
                <span className="text-xs text-slate-500">Similaridade: {result.similarity}% (mínimo: {result.threshold}%)</span>
              </div>,
              { duration: 8000 }
            );
            setVerifyingFace(false);
            setUploadingPhoto(false);
            return; // Não continua com o registro
          }
          
          toast.dismiss("face-verify");
          toast.success(
            <div className="flex flex-col gap-1">
              <span className="font-bold">✅ Identidade verificada!</span>
              <span className="text-xs text-green-600">Similaridade: {result.similarity.toFixed(1)}%</span>
            </div>,
            { duration: 3000 }
          );
          
        } catch (verifyError) {
          toast.dismiss("face-verify");
          console.error("Erro na verificação facial:", verifyError);
          
          // Se o erro for porque não há foto cadastrada, permite continuar
          if (verifyError.response?.data?.detail?.includes("sem foto cadastrada") || 
              verifyError.response?.data?.detail?.includes("verificação ignorada")) {
            toast.warning("⚠️ Funcionário sem foto de cadastro. Verificação facial ignorada.", { duration: 4000 });
          } else {
            toast.error(
              verifyError.response?.data?.detail || "Erro na verificação facial. Tente novamente.",
              { duration: 5000 }
            );
            setVerifyingFace(false);
            setUploadingPhoto(false);
            return;
          }
        } finally {
          setVerifyingFace(false);
        }
      }
      
      // Upload photo to Cloudflare Images
      const uploadResponse = await axios.post(`${API}/upload-image-base64`, {
        image: capturedPhoto
      });
      
      const photoUrl = uploadResponse.data.image_url;
      
      // Register time record with photo
      const recordData = {
        record_type: pendingRecordType,
        timestamp: new Date().toISOString(),
        latitude: location?.latitude,
        longitude: location?.longitude,
        photo_url: photoUrl,
        notes: null
      };

      if (isOnline) {
        // Online: enviar diretamente para o servidor
        const response = await axios.post(`${API}/time-records`, recordData);
        const newRecord = response.data;
        
        toast.success(
          <div className="flex flex-col gap-2">
            <span>{pendingRecordType === "entry" ? "✅ Entrada registrada com foto!" : "✅ Saída registrada com foto!"}</span>
            <button 
              onClick={() => downloadReceipt(newRecord.id)}
              className="text-sm underline text-ciaponto-cyan hover:text-ciaponto-navy"
            >
              📄 Baixar Comprovante
            </button>
          </div>,
          { duration: 8000 }
        );
      } else {
        // Offline: salvar localmente
        saveOfflineRecord(recordData);
        updatePendingRecords();
        toast.success(
          pendingRecordType === "entry" 
            ? "📴 Entrada salva offline! Será sincronizada quando houver conexão." 
            : "📴 Saída salva offline! Será sincronizada quando houver conexão."
        );
      }
      
      // Reset states
      setShowCamera(false);
      setCapturedPhoto(null);
      setPendingRecordType(null);
      fetchTodayRecords();
      
    } catch (error) {
      // Se falhar online, tentar salvar offline
      if (!isOnline || error.code === "ERR_NETWORK") {
        const recordData = {
          record_type: pendingRecordType,
          timestamp: new Date().toISOString(),
          latitude: location?.latitude,
          longitude: location?.longitude,
          photo_url: capturedPhoto,
          notes: null
        };
        saveOfflineRecord(recordData);
        updatePendingRecords();
        toast.warning("📴 Sem conexão. Registro salvo localmente para sincronizar depois.");
        
        setShowCamera(false);
        setCapturedPhoto(null);
        setPendingRecordType(null);
      } else {
        toast.error(error.response?.data?.detail || "Erro ao registrar ponto");
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleClockIn = async (type) => {
    // OBRIGATÓRIO: Verificar se o GPS está ativado e com coordenadas
    if (!location || !location.latitude || !location.longitude) {
      // Tentar obter localização
      setGettingLocation(true);
      
      if (!("geolocation" in navigator)) {
        toast.error(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">❌ GPS não disponível</span>
            <span className="text-sm">Seu dispositivo não suporta geolocalização.</span>
          </div>,
          { duration: 5000 }
        );
        setGettingLocation(false);
        return;
      }
      
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            { 
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        });
        
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setGettingLocation(false);
        
        // Agora sim, iniciar a câmera
        startCamera(type);
        
      } catch (error) {
        setGettingLocation(false);
        
        let errorMessage = "Não foi possível obter sua localização.";
        let errorTip = "Verifique se o GPS está ativado.";
        
        if (error.code === 1) {
          // PERMISSION_DENIED
          errorMessage = "Permissão de localização negada.";
          errorTip = "Vá em Configurações > Aplicativos > Navegador > Permissões e ative a Localização.";
        } else if (error.code === 2) {
          // POSITION_UNAVAILABLE
          errorMessage = "GPS indisponível.";
          errorTip = "Ative o GPS do seu celular e tente novamente.";
        } else if (error.code === 3) {
          // TIMEOUT
          errorMessage = "Tempo esgotado ao obter localização.";
          errorTip = "Vá para um local com melhor sinal de GPS.";
        }
        
        toast.error(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">❌ {errorMessage}</span>
            <span className="text-sm">{errorTip}</span>
            <span className="text-xs mt-1 text-orange-200">O GPS é obrigatório para registrar o ponto.</span>
          </div>,
          { duration: 8000 }
        );
        return;
      }
    } else {
      // Já tem localização, iniciar câmera
      startCamera(type);
    }
  };

  // Função para download do comprovante digital (Portaria 671/2021)
  const downloadReceipt = async (recordId) => {
    try {
      const response = await axios.get(`${API}/compliance/receipt/${recordId}`);
      const { receipt, verification } = response.data;
      
      // Gerar conteúdo do comprovante em texto
      const receiptContent = `
════════════════════════════════════════════════════════════
         COMPROVANTE DE REGISTRO DE PONTO
           (Portaria 671/2021 - REP-P)
════════════════════════════════════════════════════════════

EMPRESA
${receipt.company?.trade_name || receipt.company?.name || 'N/A'}
CNPJ: ${receipt.company?.cnpj || 'N/A'}

────────────────────────────────────────────────────────────

FUNCIONÁRIO
Nome: ${receipt.employee?.name || 'N/A'}
CPF: ${receipt.employee?.cpf || 'N/A'}
Cargo: ${receipt.employee?.position || 'N/A'}

────────────────────────────────────────────────────────────

REGISTRO
NSR: ${receipt.nsr || 'N/A'}
Tipo: ${receipt.record_type}
Data/Hora: ${new Date(receipt.timestamp).toLocaleString('pt-BR')}
Fonte: ${receipt.source?.toUpperCase() || 'WEB'}

────────────────────────────────────────────────────────────

LOCALIZAÇÃO
Endereço: ${receipt.location?.address || 'N/A'}
Coordenadas: ${receipt.location?.latitude || 'N/A'}, ${receipt.location?.longitude || 'N/A'}
Status Cerca: ${receipt.geofence_status === 'inside' ? 'DENTRO DA ÁREA' : 
               receipt.geofence_status === 'outside' ? 'FORA DA ÁREA' : 
               receipt.geofence_status === 'not_configured' ? 'NÃO CONFIGURADA' : 'N/A'}

────────────────────────────────────────────────────────────

VERIFICAÇÃO DE AUTENTICIDADE
Hash: ${receipt.timestamp_hash?.substring(0, 32)}...
ID do Registro: ${receipt.id}

Este comprovante é autêntico e pode ser verificado através
do hash de integridade no sistema CiaPonto.

────────────────────────────────────────────────────────────

Comprovante gerado em: ${new Date(receipt.generated_at).toLocaleString('pt-BR')}
Sistema REP-P em conformidade com a Portaria 671/2021 MTE

════════════════════════════════════════════════════════════
      `;
      
      // Criar e baixar arquivo
      const blob = new Blob([receiptContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprovante_ponto_${receipt.nsr || receipt.id}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("📄 Comprovante baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao baixar comprovante:", error);
      toast.error("Erro ao baixar comprovante. Tente novamente.");
    }
  };

  const lastRecord = todayRecords[0];
  // No modo offline, habilitar ambos os botões para o funcionário poder registrar entrada ou saída
  const canClockIn = !isOnline || !lastRecord || lastRecord.record_type === "exit";
  const canClockOut = !isOnline || (lastRecord && lastRecord.record_type === "entry");

  return (
    <div className="space-y-6" data-testid="clock-in-out-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Registrar Ponto</h1>
        <p className="text-slate-600">Registre sua entrada ou saída</p>
      </div>

      {/* Connection Status */}
      <Card className={`border-2 ${isOnline ? "border-green-200 bg-green-50" : "border-orange-200 bg-orange-50"}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isOnline ? "bg-green-200" : "bg-orange-200"
            }`}>
              {isOnline ? (
                <Wifi className="w-5 h-5 text-green-700" />
              ) : (
                <WifiOff className="w-5 h-5 text-orange-700" />
              )}
            </div>
            <div className="flex-1">
              <p className={`font-medium ${isOnline ? "text-green-800" : "text-orange-800"}`}>
                {isOnline ? "Online" : "Offline - Modo local ativado"}
              </p>
              <p className={`text-sm ${isOnline ? "text-green-600" : "text-orange-600"}`}>
                {isOnline 
                  ? "Registros serão enviados em tempo real" 
                  : "Registros serão salvos e sincronizados quando houver conexão"
                }
              </p>
            </div>
            {!isOnline && pendingOfflineRecords.length > 0 && (
              <div className="flex items-center gap-2">
                <CloudOff className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">
                  {pendingOfflineRecords.length} pendente(s)
                </span>
              </div>
            )}
            {isOnline && pendingOfflineRecords.length > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={syncOfflineRecords}
                disabled={syncing}
                className="border-green-300 text-green-700 hover:bg-green-100"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Sincronizar ({pendingOfflineRecords.length})
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Location Status - GPS OBRIGATÓRIO */}
      <Card className={`border ${location ? "border-green-200 bg-green-50" : "border-orange-300 bg-orange-50"}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              location ? "bg-green-100" : "bg-orange-100"
            }`}>
              <MapPin className={`w-5 h-5 ${location ? "text-green-600" : "text-orange-600"}`} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900">
                {gettingLocation ? "Obtendo localização..." : location ? "✓ GPS Ativo" : "⚠️ GPS Desativado"}
              </p>
              {location ? (
                <p className="text-sm text-green-600">
                  Coordenadas: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
              ) : (
                <p className="text-sm text-orange-600 font-medium">
                  Ative o GPS para registrar o ponto!
                </p>
              )}
              {locationError && <p className="text-sm text-red-500 mt-1">{locationError}</p>}
            </div>
            {!location && !gettingLocation && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={getLocation}
                className="bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200"
              >
                <MapPin className="w-4 h-4 mr-1" />
                Ativar GPS
              </Button>
            )}
            {gettingLocation && (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <div className="w-full max-w-md mx-auto">
            {!capturedPhoto ? (
              <div className="relative">
                {/* Vídeo da câmera */}
                <div className="relative aspect-[3/4] bg-black overflow-hidden">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted
                    className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""}`}
                  />
                  
                  {/* Overlay escuro com recorte oval para o rosto */}
                  <div className="absolute inset-0 pointer-events-none">
                    <svg className="w-full h-full" viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
                      <defs>
                        <mask id="faceMask">
                          <rect width="100%" height="100%" fill="white"/>
                          <ellipse cx="150" cy="160" rx="85" ry="110" fill="black"/>
                        </mask>
                      </defs>
                      <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#faceMask)"/>
                      {/* Borda da elipse */}
                      <ellipse 
                        cx="150" 
                        cy="160" 
                        rx="85" 
                        ry="110" 
                        fill="none" 
                        stroke="white" 
                        strokeWidth="3"
                        strokeDasharray="8 4"
                        className="animate-pulse"
                      />
                      {/* Marcadores de alinhamento */}
                      <line x1="150" y1="40" x2="150" y2="60" stroke="white" strokeWidth="2" opacity="0.7"/>
                      <line x1="150" y1="260" x2="150" y2="280" stroke="white" strokeWidth="2" opacity="0.7"/>
                      <line x1="55" y1="160" x2="75" y2="160" stroke="white" strokeWidth="2" opacity="0.7"/>
                      <line x1="225" y1="160" x2="245" y2="160" stroke="white" strokeWidth="2" opacity="0.7"/>
                    </svg>
                  </div>
                  
                  {/* Header com informações */}
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 pb-8">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${pendingRecordType === "entry" ? "bg-green-500" : "bg-red-500"} animate-pulse`}></div>
                        <span className="text-white text-sm font-medium">
                          {pendingRecordType === "entry" ? "Registro de Entrada" : "Registro de Saída"}
                        </span>
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded text-xs">
                        {facingMode === "user" ? "Câmera Frontal" : "Câmera Traseira"}
                      </div>
                    </div>
                  </div>
                  
                  {/* Instruções centrais */}
                  <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex flex-col items-center pointer-events-none" style={{marginTop: '120px'}}>
                    <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                      👤 Centralize seu rosto no oval
                    </div>
                  </div>
                  
                  {/* Footer com botões */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 pt-12">
                    {/* Indicador de Flash */}
                    {flashEnabled && (
                      <div className="flex justify-center mb-4">
                        <div className="bg-yellow-500/90 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Sun className="w-3 h-3" />
                          Flash Ativo
                        </div>
                      </div>
                    )}
                    
                    {/* Dica de iluminação */}
                    <div className="text-center mb-4">
                      <p className="text-white/80 text-xs">
                        💡 Mantenha boa iluminação no rosto
                      </p>
                    </div>
                    
                    {/* Botões de ação */}
                    <div className="flex items-center justify-center gap-6">
                      {/* Botão Cancelar */}
                      <button
                        onClick={cancelCamera}
                        className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                      >
                        <X className="w-6 h-6 text-white" />
                      </button>
                      
                      {/* Botão Capturar (grande) */}
                      <button
                        onClick={capturePhoto}
                        className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform border-4 border-white/50"
                      >
                        <div className={`w-16 h-16 rounded-full ${pendingRecordType === "entry" ? "bg-green-500" : "bg-red-500"} flex items-center justify-center`}>
                          <Camera className="w-8 h-8 text-white" />
                        </div>
                      </button>
                      
                      {/* Botão Alternar Câmera */}
                      <button
                        onClick={toggleCamera}
                        className="w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                      >
                        <RefreshCw className="w-6 h-6 text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Tela de confirmação da foto */
              <div className="bg-[#137082] p-4 rounded-lg mx-4">
                <div className="text-center mb-4">
                  <h3 className="text-white text-lg font-semibold">
                    {pendingRecordType === "entry" ? "✅ Confirmar Entrada" : "✅ Confirmar Saída"}
                  </h3>
                  <p className="text-slate-400 text-sm">Verifique se a foto está nítida</p>
                </div>
                
                <div className="relative aspect-[3/4] bg-black rounded-lg overflow-hidden mb-4">
                  <img 
                    src={capturedPhoto} 
                    alt="Foto capturada" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                    ✓ Foto OK
                  </div>
                  
                  {/* Mostrar resultado da verificação facial */}
                  {faceVerificationResult && (
                    <div className={`absolute bottom-3 left-3 right-3 ${faceVerificationResult.verified ? 'bg-green-500/90' : 'bg-red-500/90'} text-white px-3 py-2 rounded-lg text-sm`}>
                      {faceVerificationResult.verified ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>Identidade verificada ({faceVerificationResult.similarity?.toFixed(0)}%)</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          <span>{faceVerificationResult.message}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={retakePhoto} 
                    className="flex-1 bg-transparent border-slate-600 text-white hover:bg-[#17889E]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Tirar Outra
                  </Button>
                  <Button 
                    onClick={uploadPhotoAndRegister} 
                    disabled={uploadingPhoto}
                    className={`flex-1 ${pendingRecordType === "entry" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                  >
                    {uploadingPhoto ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verificando...
                      </>
                    ) : verifyingFace ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verificando Rosto...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirmar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />

      {/* Aviso de Modo Offline */}
      {!isOnline && (
        <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-center">
          <p className="text-sm text-orange-800">
            <strong>📴 Modo Offline:</strong> Ambos os botões estão habilitados. Os registros serão sincronizados quando a conexão voltar.
          </p>
        </div>
      )}

      {/* Aviso de Ponto Bloqueado */}
      {!checkingPermission && !pontoLiberado && (
        <div className="bg-red-100 border-2 border-red-300 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">🔒</span>
            </div>
            <div>
              <h3 className="font-bold text-lg text-red-800 mb-1">Ponto Bloqueado</h3>
              <p className="text-red-700 mb-2">
                Seu registro de ponto ainda não foi liberado pela empresa.
              </p>
              <p className="text-sm text-red-600">
                Entre em contato com o administrador para liberar seu acesso ao registro de ponto.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Clock Buttons */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className={`border-2 transition-all ${canClockIn && pontoLiberado ? "border-green-200 hover:border-green-400" : "border-slate-200 opacity-50"}`}>
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Entrada</h3>
            <p className="text-sm text-slate-500 mb-4">
              {!pontoLiberado ? "🔒 Aguardando liberação" : "Registrar início do expediente com foto"}
            </p>
            <Button
              className="w-full bg-green-600 hover:bg-green-700 h-12"
              onClick={() => handleClockIn("entry")}
              disabled={!canClockIn || loading || showCamera || !pontoLiberado}
              data-testid="clock-in-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              {!pontoLiberado ? "Bloqueado" : "Registrar Entrada"}
            </Button>
          </CardContent>
        </Card>

        <Card className={`border-2 transition-all ${canClockOut && pontoLiberado ? "border-red-200 hover:border-red-400" : "border-slate-200 opacity-50"}`}>
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingDown className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Saída</h3>
            <p className="text-sm text-slate-500 mb-4">
              {!pontoLiberado ? "🔒 Aguardando liberação" : "Registrar fim do expediente com foto"}
            </p>
            <Button
              className="w-full bg-red-600 hover:bg-red-700 h-12"
              onClick={() => handleClockIn("exit")}
              disabled={!canClockOut || loading || showCamera || !pontoLiberado}
              data-testid="clock-out-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              {!pontoLiberado ? "Bloqueado" : "Registrar Saída"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Today's Records */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Registros de Hoje</CardTitle>
        </CardHeader>
        <CardContent>
          {todayRecords.length > 0 ? (
            <div className="space-y-3">
              {todayRecords.map((record, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      record.record_type === "entry" ? "bg-green-100" : "bg-red-100"
                    }`}>
                      {record.record_type === "entry" ? (
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {record.record_type === "entry" ? "Entrada" : "Saída"}
                      </p>
                      <p className="text-sm text-slate-500">
                        {new Date(record.timestamp).toLocaleTimeString("pt-BR")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {record.photo_url && (
                      <div className="flex items-center gap-1 text-sm text-ciaponto-cyan">
                        <Camera className="w-4 h-4" />
                        <span>Foto</span>
                      </div>
                    )}
                    {record.latitude && (
                      <div className="flex items-center gap-1 text-sm text-green-600">
                        <MapPin className="w-4 h-4" />
                        <span>GPS</span>
                      </div>
                    )}
                    <button
                      onClick={() => downloadReceipt(record.id)}
                      className="flex items-center gap-1 text-sm text-slate-500 hover:text-ciaponto-cyan transition-colors"
                      title="Baixar Comprovante"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">Nenhum registro hoje</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// My Records Component
function MyRecords() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/time-records`);
      setRecords(response.data);
    } catch (error) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  };

  // Group records by date
  const groupedRecords = records.reduce((groups, record) => {
    const date = record.timestamp.split("T")[0];
    if (!groups[date]) groups[date] = [];
    groups[date].push(record);
    return groups;
  }, {});

  return (
    <div className="space-y-6" data-testid="my-records-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Meus Registros</h1>
        <p className="text-slate-600">Histórico de pontos registrados</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : Object.keys(groupedRecords).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(groupedRecords).map(([date, dayRecords]) => (
            <Card key={date} className="border border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">
                  {new Date(date + "T12:00:00").toLocaleDateString("pt-BR", { 
                    weekday: "long", 
                    day: "numeric", 
                    month: "long" 
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dayRecords.map((record, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          record.record_type === "entry" ? "bg-green-500" : "bg-red-500"
                        }`} />
                        <span className="text-sm font-medium text-slate-700">
                          {record.record_type === "entry" ? "Entrada" : "Saída"}
                        </span>
                      </div>
                      <span className="text-sm text-slate-600">
                        {new Date(record.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nenhum registro encontrado</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Hours Balance Component
function HoursBalance() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState("monthly"); // weekly, monthly, yearly
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [feriadosAtivos, setFeriadosAtivos] = useState([]);
  const [manualBankHoursAdjustment, setManualBankHoursAdjustment] = useState(0);
  const [totalHistoryBalance, setTotalHistoryBalance] = useState(null);
  
  // Estados para relatório detalhado
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [detailedReportData, setDetailedReportData] = useState(null);
  const [detailedReportLoading, setDetailedReportLoading] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];
  
  const weekDayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  useEffect(() => {
    fetchData();
    fetchFeriados();
    fetchBankHoursAdjustments();
    fetchTotalHistoryBalance();
  }, []);
  
  // Buscar saldo mensal/anual quando período mudar
  useEffect(() => {
    if (periodType === "monthly") {
      fetchMonthlyBalance(month, year);
    } else if (periodType === "weekly") {
      // Para semanal, buscar o mês atual
      fetchMonthlyBalance(month, year);
    } else if (periodType === "yearly") {
      // Para anual, buscar saldo anual (soma de todos os meses)
      fetchYearlyBalance(year);
    }
  }, [month, year, periodType]);
  
  // Buscar saldo total de todos os meses
  const fetchTotalHistoryBalance = async () => {
    try {
      const response = await axios.get(`${API}/employees/me/hours-history`);
      setTotalHistoryBalance(response.data);
    } catch (error) {
      console.log("Erro ao buscar histórico de horas:", error);
      setTotalHistoryBalance(null);
    }
  };
  
  // Estado para saldo mensal do backend
  const [monthlyBalanceFromBackend, setMonthlyBalanceFromBackend] = useState(null);
  
  // Estado para saldo anual do backend
  const [yearlyBalanceFromBackend, setYearlyBalanceFromBackend] = useState(null);
  
  // Buscar saldo mensal do backend (fonte única de verdade)
  const fetchMonthlyBalance = async (targetMonth, targetYear) => {
    try {
      const response = await axios.get(`${API}/employees/me/monthly-balance?month=${targetMonth}&year=${targetYear}`);
      setMonthlyBalanceFromBackend(response.data);
      return response.data;
    } catch (error) {
      console.log("Erro ao buscar saldo mensal:", error);
      setMonthlyBalanceFromBackend(null);
      return null;
    }
  };
  
  // Buscar saldo anual do backend (soma de todos os meses)
  const fetchYearlyBalance = async (targetYear) => {
    try {
      const response = await axios.get(`${API}/employees/me/yearly-balance?year=${targetYear}`);
      setYearlyBalanceFromBackend(response.data);
      return response.data;
    } catch (error) {
      console.log("Erro ao buscar saldo anual:", error);
      setYearlyBalanceFromBackend(null);
      return null;
    }
  };
  
  // Buscar ajustes de banco de horas do funcionário
  const fetchBankHoursAdjustments = async () => {
    try {
      const response = await axios.get(`${API}/employees/me/bank-hours`);
      setManualBankHoursAdjustment(response.data.manual_adjustment_balance || 0);
    } catch (error) {
      console.log("Erro ao buscar ajustes de banco de horas:", error);
      setManualBankHoursAdjustment(0);
    }
  };
  
  const fetchFeriados = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${API}/holidays/company?year=${currentYear}`);
      const ativos = (response.data.feriados || [])
        .filter(f => f.ativo)
        .map(f => f.data);
      setFeriadosAtivos(ativos);
    } catch (error) {
      console.log("Erro ao buscar feriados:", error);
    }
  };

  const fetchData = async () => {
    try {
      const recordsRes = await axios.get(`${API}/time-records`);
      setRecords(recordsRes.data);
      
      // Fetch employee's own schedule
      try {
        const scheduleRes = await axios.get(`${API}/employees/me/schedule`);
        setEmployeeData(scheduleRes.data);
      } catch (e) {
        console.log("Não foi possível carregar escala do funcionário");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para buscar relatório detalhado
  const fetchDetailedReport = async () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Selecione as datas de início e fim");
      return;
    }
    
    setDetailedReportLoading(true);
    try {
      const response = await axios.get(`${API}/employees/me/detailed-report?start_date=${customStartDate}&end_date=${customEndDate}`);
      setDetailedReportData(response.data);
      setShowDetailedReport(true);
    } catch (error) {
      console.error("Erro ao buscar relatório detalhado:", error);
      toast.error("Erro ao gerar relatório");
    } finally {
      setDetailedReportLoading(false);
    }
  };
  
  // Função para gerar PDF do relatório detalhado
  const generateDetailedPDF = () => {
    if (!detailedReportData) return;
    
    const doc = new jsPDF();
    const data = detailedReportData;
    
    // Título
    doc.setFontSize(18);
    doc.text("Relatório Detalhado de Horas", 14, 22);
    
    doc.setFontSize(11);
    doc.text(`Funcionário: ${data.employee?.name || "N/A"}`, 14, 32);
    doc.text(`Período: ${customStartDate} a ${customEndDate}`, 14, 38);
    
    // Resumo
    doc.setFontSize(12);
    doc.text("Resumo", 14, 50);
    
    const summary = data.summary || {};
    doc.setFontSize(10);
    doc.text(`Trabalhadas: ${summary.total_worked_formatted || "0h 00min"}`, 14, 58);
    doc.text(`Esperadas: ${summary.total_expected_formatted || "0h 00min"}`, 14, 64);
    doc.text(`Saldo: ${summary.balance_formatted || "0h 00min"}`, 14, 70);
    doc.text(`Dias: ${summary.days_worked || 0}/${summary.total_days || 0}`, 14, 76);
    
    // Tabela de registros diários
    const tableData = (data.daily_records || []).map(day => {
      const entries = day.entries || [];
      const exits = day.exits || [];
      return [
        day.date_formatted || day.date,
        day.weekday || "",
        entries[0] || "-",
        exits[0] || "-",
        entries[1] || "-",
        exits[1] || "-",
        day.worked_formatted || "-",
        day.expected_formatted || "-",
        day.status || ""
      ];
    });
    
    autoTable(doc, {
      startY: 85,
      head: [["Data", "Dia", "Ent 1", "Saí 1", "Ent 2", "Saí 2", "Trab.", "Esp.", "Status"]],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Gerado em: ${new Date().toLocaleString("pt-BR")} - Página ${i} de ${pageCount}`,
        14,
        doc.internal.pageSize.height - 10
      );
    }
    
    doc.save(`relatorio_detalhado_${customStartDate}_${customEndDate}.pdf`);
    toast.success("PDF gerado com sucesso!");
  };
  
  // Inicializar datas customizadas com o mês atual
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setCustomStartDate(firstDay.toISOString().split("T")[0]);
    setCustomEndDate(lastDay.toISOString().split("T")[0]);
  }, []);
  
  // Get work days from employee data or default to 6x1
  const getWorkDays = () => {
    return employeeData?.work_days || [1, 2, 3, 4, 5, 6]; // Default 6x1
  };
  
  // Get weekend rotation setting
  const getWeekendRotation = () => {
    return employeeData?.weekend_rotation || "none";
  };
  
  // Check if a date is in a work weekend based on rotation
  const isWorkWeekend = (date) => {
    const rotation = getWeekendRotation();
    if (rotation === "none") return true; // No rotation, all weekends work
    
    // Get week number of the month (1-indexed)
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = date.getDate();
    const weekNumber = Math.ceil((dayOfMonth + firstDayOfMonth.getDay()) / 7);
    
    if (rotation === "odd") return weekNumber % 2 === 1; // Odd weeks: 1, 3, 5
    if (rotation === "even") return weekNumber % 2 === 0; // Even weeks: 2, 4
    return true;
  };
  
  // Format schedule for display
  const formatSchedule = () => {
    const workDays = getWorkDays();
    const rotation = getWeekendRotation();
    const workScheduleType = employeeData?.work_schedule_type || "standard";
    let base = "";
    
    // Se for escala 44h comercial, mostrar isso
    if (workScheduleType === "44h_semana_comercial") {
      base = "44h Comercial (Seg-Sex 8h + Sáb 4h)";
    } else if (workDays.length === 7) {
      base = "Todos os dias";
    } else if (JSON.stringify([...workDays].sort()) === JSON.stringify([1, 2, 3, 4, 5, 6])) {
      base = "6x1 (Seg-Sáb)";
    } else if (JSON.stringify([...workDays].sort()) === JSON.stringify([1, 2, 3, 4, 5])) {
      base = "5x2 (Seg-Sex)";
    } else {
      base = workDays.map(d => weekDayNames[d]).join(", ");
    }
    
    if (rotation === "odd") return `${base} + FDS ímpares`;
    if (rotation === "even") return `${base} + FDS pares`;
    return base;
  };

  // Filter records by period
  const getFilteredRecords = () => {
    const now = new Date();
    let filtered = [...records];
    
    if (periodType === "weekly") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(r => new Date(r.timestamp) >= weekAgo);
    } else if (periodType === "monthly") {
      filtered = filtered.filter(r => {
        const recordDate = new Date(r.timestamp);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });
    } else if (periodType === "yearly") {
      filtered = filtered.filter(r => {
        const recordDate = new Date(r.timestamp);
        return recordDate.getFullYear() === year;
      });
    }
    
    return filtered;
  };

  // Calculate hours from records
  const calculateBalance = () => {
    // IMPORTANTE: Se ponto NÃO está liberado, não contabilizar horas esperadas/déficit
    const pontoLiberado = employeeData?.ponto_liberado || false;
    const pontoLiberadoAt = employeeData?.ponto_liberado_at ? new Date(employeeData.ponto_liberado_at) : null;
    
    // Se ponto não está liberado, retornar tudo zerado
    if (!pontoLiberado) {
      return {
        worked: "0.0",
        expected: 0,
        balance: "0.0",
        overtime: "0.0",
        deficit: "0.0",
        daysWorked: 0,
        scheduledDays: 0,
        isPositive: true,
        progress: 0,
        pontoLiberado: false
      };
    }
    
    const filteredRecords = getFilteredRecords();
    let totalWorkedMinutes = 0;
    
    // Group by day
    const recordsByDay = {};
    filteredRecords.forEach(r => {
      const day = new Date(r.timestamp).toDateString();
      if (!recordsByDay[day]) recordsByDay[day] = [];
      recordsByDay[day].push(r);
    });
    
    const daysWorked = Object.keys(recordsByDay).length;
    
    // Calculate hours per day
    Object.values(recordsByDay).forEach(dayRecords => {
      const sorted = dayRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      for (let i = 0; i < sorted.length - 1; i += 2) {
        if (sorted[i].record_type === "entry" && sorted[i + 1]?.record_type === "exit") {
          const entry = new Date(sorted[i].timestamp);
          const exit = new Date(sorted[i + 1].timestamp);
          totalWorkedMinutes += (exit - entry) / (1000 * 60);
        }
      }
    });
    
    const totalWorkedHours = totalWorkedMinutes / 60;
    
    // Calculate expected hours based on employee schedule
    const workDays = getWorkDays();
    
    // Count scheduled work days in the period
    let scheduledDays = 0;
    const processedDays = new Set();
    
    // Helper to check if a day should be worked (considering weekend rotation)
    const shouldWorkDay = (date, dayOfWeek) => {
      if (!workDays.includes(dayOfWeek)) return false;
      
      // If it's a weekend day (Sat or Sun), check rotation
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return isWorkWeekend(date);
      }
      return true;
    };
    
    // Data de referência para início da contagem:
    // Usar a MAIOR data entre: cadastro do funcionário E liberação do ponto
    const employeeCreatedAt = employeeData?.created_at 
      ? new Date(employeeData.created_at) 
      : new Date(2000, 0, 1); // Fallback muito antigo se não tiver
    
    // Data de liberação do ponto (só conta horas/faltas a partir desta data)
    const liberadoDate = pontoLiberadoAt 
      ? new Date(pontoLiberadoAt.getFullYear(), pontoLiberadoAt.getMonth(), pontoLiberadoAt.getDate())
      : new Date(employeeCreatedAt.getFullYear(), employeeCreatedAt.getMonth(), employeeCreatedAt.getDate());
    
    const createdDate = new Date(employeeCreatedAt.getFullYear(), employeeCreatedAt.getMonth(), employeeCreatedAt.getDate());
    
    // Usar a data mais recente entre cadastro e liberação do ponto
    const startCountingDate = liberadoDate > createdDate ? liberadoDate : createdDate;
    
    // Função auxiliar para verificar se a data é após a liberação do ponto
    const isAfterPontoLiberado = (date) => {
      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return checkDate >= startCountingDate;
    };
    
    // For each day that has records, check if it was a scheduled work day
    Object.keys(recordsByDay).forEach(dayStr => {
      const date = new Date(dayStr);
      const dayOfWeek = date.getDay();
      // Só conta se for após a liberação do ponto
      if (isAfterPontoLiberado(date) && shouldWorkDay(date, dayOfWeek)) {
        scheduledDays++;
      }
      processedDays.add(dayStr);
    });
    
    // Also count scheduled days without records (missing days)
    // Importante: começar a contar apenas a partir da data de liberação do ponto
    
    // Função para verificar se uma data é um feriado ativo
    const isFeriadoAtivo = (date) => {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      return feriadosAtivos.includes(dateStr);
    };
    
    // Contador de horas de feriados creditadas
    let horasFeriadosCreditadas = 0;
    let feriadosContados = 0;
    
    if (periodType === "monthly") {
      const daysInMonth = new Date(year, month, 0).getDate();
      
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month - 1, d);
        
        // Não contar dias antes da liberação do ponto
        if (!isAfterPontoLiberado(date)) {
          continue;
        }
        
        if (date > new Date()) break; // Don't count future days
        const dayOfWeek = date.getDay();
        const dayStr = date.toDateString();
        
        // Verificar se é feriado ativo
        if (isFeriadoAtivo(date) && shouldWorkDay(date, dayOfWeek)) {
          // É feriado em dia de trabalho - creditar horas automaticamente
          // Verificar se já tem registro nesse dia
          const temRegistro = processedDays.has(dayStr);
          if (!temRegistro) {
            horasFeriadosCreditadas += 8;
            feriadosContados++;
            // Conta como dia trabalhado para não aparecer como falta
            processedDays.add(dayStr);
          }
          // Feriado conta como dia programado
          scheduledDays++;
        } else if (shouldWorkDay(date, dayOfWeek) && !processedDays.has(dayStr)) {
          scheduledDays++;
        }
      }
    } else if (periodType === "weekly") {
      // Para período semanal, verificar os últimos 7 dias
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        
        // Não contar dias antes da liberação do ponto
        if (!isAfterPontoLiberado(date)) {
          continue;
        }
        
        if (date > new Date()) break;
        const dayOfWeek = date.getDay();
        const dayStr = date.toDateString();
        
        // Verificar se é feriado ativo
        if (isFeriadoAtivo(date) && shouldWorkDay(date, dayOfWeek)) {
          const temRegistro = processedDays.has(dayStr);
          if (!temRegistro) {
            horasFeriadosCreditadas += 8;
            feriadosContados++;
            processedDays.add(dayStr);
          }
          scheduledDays++;
        } else if (shouldWorkDay(date, dayOfWeek) && !processedDays.has(dayStr)) {
          scheduledDays++;
        }
      }
    } else if (periodType === "yearly") {
      // Para período anual, verificar todos os dias do ano
      const daysInYear = (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 366 : 365;
      
      for (let d = 1; d <= daysInYear; d++) {
        const date = new Date(year, 0, d);
        
        // Não contar dias antes da liberação do ponto
        if (!isAfterPontoLiberado(date)) {
          continue;
        }
        
        if (date > new Date()) break;
        const dayOfWeek = date.getDay();
        const dayStr = date.toDateString();
        
        // Verificar se é feriado ativo
        if (isFeriadoAtivo(date) && shouldWorkDay(date, dayOfWeek)) {
          const temRegistro = processedDays.has(dayStr);
          if (!temRegistro) {
            horasFeriadosCreditadas += 8;
            feriadosContados++;
            processedDays.add(dayStr);
          }
          scheduledDays++;
        } else if (shouldWorkDay(date, dayOfWeek) && !processedDays.has(dayStr)) {
          scheduledDays++;
        }
      }
    }
    
    // Adicionar horas de feriados ao total trabalhado
    const totalComFeriados = totalWorkedHours + horasFeriadosCreditadas;
    
    // Calcular horas esperadas considerando o tipo de escala
    let expectedHours = 0;
    const workScheduleType = employeeData?.work_schedule_type || "standard";
    
    if (workScheduleType === "44h_semana_comercial") {
      // Escala 44h comercial: 8h seg-sex + 4h sábado
      // Precisamos recalcular baseado nos dias efetivamente trabalhados
      if (periodType === "monthly") {
        const daysInMonth = new Date(year, month, 0).getDate();
        const today = new Date();
        
        for (let d = 1; d <= daysInMonth; d++) {
          const date = new Date(year, month - 1, d);
          
          // Não contar dias futuros
          if (date > today) break;
          
          const dayOfWeek = date.getDay();
          
          // Verificar se é dia de trabalho e após liberação do ponto
          if (isAfterPontoLiberado(date) && shouldWorkDay(date, dayOfWeek)) {
            // Não contar feriados
            if (!isFeriadoAtivo(date)) {
              if (dayOfWeek === 6) { // Sábado
                expectedHours += 4; // 4h no sábado
              } else {
                expectedHours += 8; // 8h nos outros dias
              }
            }
          }
        }
      } else if (periodType === "weekly") {
        const now = new Date();
        for (let i = 0; i < 7; i++) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dayOfWeek = date.getDay();
          
          if (isAfterPontoLiberado(date) && shouldWorkDay(date, dayOfWeek)) {
            if (!isFeriadoAtivo(date)) {
              if (dayOfWeek === 6) { // Sábado
                expectedHours += 4;
              } else {
                expectedHours += 8;
              }
            }
          }
        }
      } else {
        // Para outros períodos (yearly), calcular baseado nos dias programados
        // Usar proporção correta baseada nas horas semanais configuradas
        const workDays = employeeData?.work_days || [1, 2, 3, 4, 5, 6];
        const weeklyHours = employeeData?.weekly_hours || 44;
        expectedHours = scheduledDays * (weeklyHours / workDays.length);
      }
    } else {
      // Escala padrão: calcular horas por dia baseado nas horas semanais e dias de trabalho
      const weeklyHours = employeeData?.weekly_hours || 44;
      const workDays = employeeData?.work_days || [1, 2, 3, 4, 5, 6];
      const hoursPerDay = weeklyHours / workDays.length;
      expectedHours = scheduledDays * hoursPerDay;
    }
    
    // Aplicar tolerância CLT (Art. 58, §1º) - até 10 minutos diários
    const TOLERANCIA_DIARIA_MINUTOS = 10;
    let balanceMinutes = (totalComFeriados - expectedHours) * 60;
    let toleranciaAplicada = false;
    
    // Se a diferença está dentro da tolerância, considera como zero
    if (Math.abs(balanceMinutes) <= TOLERANCIA_DIARIA_MINUTOS) {
      balanceMinutes = 0;
      toleranciaAplicada = true;
    } else if (balanceMinutes > TOLERANCIA_DIARIA_MINUTOS) {
      // Hora extra: desconta a tolerância
      balanceMinutes = balanceMinutes - TOLERANCIA_DIARIA_MINUTOS;
      toleranciaAplicada = true;
    } else if (balanceMinutes < -TOLERANCIA_DIARIA_MINUTOS) {
      // Atraso: desconta a tolerância
      balanceMinutes = balanceMinutes + TOLERANCIA_DIARIA_MINUTOS;
      toleranciaAplicada = true;
    }
    
    const balance = balanceMinutes / 60;
    
    // ADICIONAR ajustes manuais de banco de horas ao saldo final
    // Usar 0 como fallback se o estado ainda não foi carregado
    const adjustment = typeof manualBankHoursAdjustment === 'number' ? manualBankHoursAdjustment : 0;
    const finalBalance = balance + adjustment;
    
    const overtime = finalBalance > 0 ? finalBalance : 0;
    const deficit = finalBalance < 0 ? Math.abs(finalBalance) : 0;
    
    // Arredondar expectedHours para evitar números como 234.666666666
    const roundedExpected = Math.round(expectedHours * 10) / 10;
    
    return {
      worked: formatHoursMinutes(totalComFeriados),
      expected: formatHoursMinutes(roundedExpected),
      balance: formatHoursMinutes(finalBalance, true),
      automaticBalance: formatHoursMinutes(balance),
      manualAdjustment: formatHoursMinutes(adjustment),
      overtime: formatHoursMinutes(overtime),
      deficit: formatHoursMinutes(deficit),
      daysWorked: daysWorked + feriadosContados,
      scheduledDays,
      isPositive: finalBalance >= 0,
      progress: roundedExpected > 0 ? Math.min(100, (totalComFeriados / roundedExpected) * 100) : 0,
      toleranciaAplicada,
      feriadosCreditados: feriadosContados,
      horasFeriados: horasFeriadosCreditadas
    };
  };

  const getPeriodLabel = () => {
    if (periodType === "weekly") return "Últimos 7 dias";
    if (periodType === "monthly") return `${months[month - 1]} ${year}`;
    if (periodType === "yearly") return `Ano ${year}`;
    return "";
  };

  // Usar useMemo para calcular balance - PRIORIZAR DADOS DO BACKEND
  const balance = useMemo(() => {
    // Para período MENSAL, usar dados do backend (fonte única de verdade)
    if (periodType === "monthly" && monthlyBalanceFromBackend) {
      const data = monthlyBalanceFromBackend;
      return {
        worked: data.worked_hours?.toString() || "0",
        expected: data.expected_hours || 0,
        balance: formatHoursMinutes(data.balance || 0, true),
        automaticBalance: formatHoursMinutes(data.automatic_balance || 0),
        manualAdjustment: formatHoursMinutes(data.manual_adjustment || 0),
        overtime: formatHoursMinutes(data.overtime || 0),
        deficit: formatHoursMinutes(data.deficit || 0),
        daysWorked: data.days_worked || 0,
        scheduledDays: data.expected_days || 0,
        isPositive: (data.balance || 0) >= 0,
        progress: data.expected_hours > 0 ? Math.min(100, (data.worked_hours / data.expected_hours) * 100) : 0,
        toleranciaAplicada: false,
        feriadosCreditados: 0,
        horasFeriados: 0,
        pontoLiberado: data.ponto_liberado
      };
    }
    
    // Para período ANUAL, usar o novo endpoint yearly-balance (soma de todos os meses)
    if (periodType === "yearly" && yearlyBalanceFromBackend) {
      const data = yearlyBalanceFromBackend;
      const totalBalance = data.total_balance || 0;
      const totalWorked = data.total_worked_hours || 0;
      const totalExpected = data.total_expected_hours || 0;
      const overtime = data.overtime || 0;
      const deficit = data.deficit || 0;
      
      return {
        worked: formatHoursMinutes(totalWorked),
        expected: formatHoursMinutes(totalExpected),
        balance: formatHoursMinutes(totalBalance, true),
        automaticBalance: formatHoursMinutes(totalBalance),
        manualAdjustment: formatHoursMinutes(data.total_adjustments || 0),
        overtime: formatHoursMinutes(overtime),
        deficit: formatHoursMinutes(deficit),
        daysWorked: 0,
        scheduledDays: 0,
        isPositive: totalBalance >= 0,
        progress: totalExpected > 0 ? Math.min(100, (totalWorked / totalExpected) * 100) : 0,
        toleranciaAplicada: false,
        feriadosCreditados: 0,
        horasFeriados: 0,
        monthlyBreakdown: data.monthly_breakdown || []
      };
    }
    
    // Para período SEMANAL ou como fallback, usar cálculo local
    return calculateBalance();
  }, [records, employeeData, periodType, month, year, feriadosAtivos, manualBankHoursAdjustment, totalHistoryBalance, monthlyBalanceFromBackend, yearlyBalanceFromBackend]);

  return (
    <div className="space-y-6" data-testid="hours-balance-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Saldo de Horas</h1>
        <p className="text-slate-600">Acompanhe suas horas trabalhadas</p>
      </div>
      
      {/* Employee Schedule Info */}
      {employeeData && (
        <Card className="border border-sky-200 bg-sky-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-ciaponto-navy">Sua Escala de Trabalho</p>
                <p className="text-lg font-bold text-sky-900">{formatSchedule()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-ciaponto-cyan">Carga horária</p>
                <p className="text-lg font-bold text-sky-900">
                  {employeeData?.work_schedule_type === "44h_semana_comercial" 
                    ? "44h/sem" 
                    : `${employeeData?.weekly_hours || 44}h/sem`}
                </p>
              </div>
            </div>
            <div className="mt-3 flex gap-1">
              {weekDayNames.map((day, idx) => (
                <div
                  key={idx}
                  className={`flex-1 text-center py-1 rounded text-xs font-medium ${
                    getWorkDays().includes(idx)
                      ? "bg-[#1CA1BA] text-white"
                      : "bg-slate-200 text-slate-400"
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>
            {getWeekendRotation() !== "none" && (
              <div className="mt-3 p-2 bg-orange-100 rounded-lg">
                <p className="text-xs font-medium text-orange-800">
                  🔄 Fim de semana alternado: {getWeekendRotation() === "odd" ? "Semanas ímpares (1ª, 3ª, 5ª)" : "Semanas pares (2ª, 4ª)"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Period Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Period Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Período</label>
              <select
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
              >
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
                <option value="yearly">Anual</option>
              </select>
            </div>

            {/* Month selector (only for monthly) */}
            {periodType === "monthly" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Mês</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                >
                  {months.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Year selector (for monthly and yearly) */}
            {(periodType === "monthly" || periodType === "yearly") && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Ano</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                >
                  {[2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Period display for weekly */}
            {periodType === "weekly" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Período</label>
                <div className="h-10 px-3 rounded-md border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-600">
                  Últimos 7 dias
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : (
        <>
          {/* Total Balance Card - Saldo acumulado de todos os meses */}
          {totalHistoryBalance && (
            <Card className={`border-2 mb-4 ${
              totalHistoryBalance.total_balance >= 0 
                ? "border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50" 
                : "border-orange-300 bg-gradient-to-r from-orange-50 to-red-50"
            }`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      totalHistoryBalance.total_balance >= 0 ? 'bg-emerald-100' : 'bg-orange-100'
                    }`}>
                      <TrendingUp className={`w-6 h-6 ${
                        totalHistoryBalance.total_balance >= 0 ? 'text-emerald-600' : 'text-orange-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Saldo Total Acumulado</p>
                      <p className="text-xs text-slate-500">Desde a liberação do ponto</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-heading font-bold text-3xl ${
                      totalHistoryBalance.total_balance >= 0 ? 'text-emerald-600' : 'text-orange-600'
                    }`}>
                      {totalHistoryBalance.total_balance >= 0 ? '+' : ''}{totalHistoryBalance.total_balance}h
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {totalHistoryBalance.total_balance >= 0 
                        ? '✅ Horas extras acumuladas' 
                        : '⏰ Horas pendentes'}
                    </p>
                  </div>
                </div>
                
                {/* Mini resumo */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-200">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Total Trabalhadas</p>
                    <p className="font-semibold text-blue-600">{totalHistoryBalance.total_worked_hours}h</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Total Esperadas</p>
                    <p className="font-semibold text-slate-600">{totalHistoryBalance.total_expected_hours}h</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Total Faltas</p>
                    <p className="font-semibold text-red-600">{totalHistoryBalance.total_absences}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Balance Card */}
          <Card className={`border-2 ${balance.isPositive ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}`}>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-600 mb-2">Saldo de Horas - {getPeriodLabel()}</p>
              <div className={`font-heading font-bold text-5xl mb-2 ${
                balance.isPositive ? "text-green-600" : "text-red-600"
              }`}>
                {balance.isPositive ? "+" : ""}{balance.balance}h
              </div>
              <p className="text-slate-500">
                {balance.isPositive 
                  ? "✅ Você está em dia com suas horas!" 
                  : "⚠️ Você tem horas a compensar"}
              </p>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Horas Trabalhadas</p>
                    <p className="font-heading font-bold text-3xl text-slate-900">{balance.worked}h</p>
                  </div>
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-ciaponto-cyan" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Horas Esperadas</p>
                    <p className="font-heading font-bold text-3xl text-slate-900">{balance.expected}h</p>
                  </div>
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Horas Extras</p>
                    <p className="font-heading font-bold text-3xl text-green-600">+{balance.overtime}h</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Déficit</p>
                    <p className="font-heading font-bold text-3xl text-red-600">-{balance.deficit}h</p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Card */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Progresso do Período</CardTitle>
              <CardDescription>
                {balance.daysWorked} dias trabalhados • {getPeriodLabel()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Horas trabalhadas vs esperadas</span>
                    <span className="font-medium">
                      {balance.worked}h / {balance.expected}h ({Math.round(balance.progress)}%)
                    </span>
                  </div>
                  <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        balance.progress >= 100 ? "bg-green-500" : 
                        balance.progress >= 80 ? "bg-[#1CA1BA]" : 
                        balance.progress >= 50 ? "bg-yellow-500" : "bg-red-500"
                      }`}
                      style={{ width: `${Math.min(100, balance.progress)}%` }}
                    />
                  </div>
                </div>
                
                {/* Legend */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-slate-600">≥100% (meta atingida)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#1CA1BA]" />
                    <span className="text-slate-600">80-99% (bom)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-slate-600">50-79% (atenção)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-slate-600">&lt;50% (crítico)</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Relatório Detalhado */}
          <Card className="border border-slate-200" data-testid="detailed-report-section">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-ciaponto-cyan" />
                Relatório Detalhado
              </CardTitle>
              <CardDescription>
                Gere um relatório dia a dia das suas horas trabalhadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Seleção de período */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Data Início</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                      data-testid="detailed-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Data Fim</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                      data-testid="detailed-end-date"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={fetchDetailedReport}
                      disabled={detailedReportLoading}
                      className="w-full bg-[#1CA1BA]-dark hover:bg-sky-700"
                      data-testid="generate-report-btn"
                    >
                      {detailedReportLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Calendar className="w-4 h-4 mr-2" />
                      )}
                      Gerar Relatório
                    </Button>
                  </div>
                </div>
                
                {/* Resultados do relatório */}
                {showDetailedReport && detailedReportData && (
                  <div className="space-y-4 mt-6" data-testid="detailed-report-results">
                    {/* Cards de resumo */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                        <p className="text-xs text-ciaponto-cyan font-medium">Trabalhadas</p>
                        <p className="text-xl font-bold text-ciaponto-navy">
                          {detailedReportData.summary?.total_worked_formatted || "0h 00min"}
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-600 font-medium">Esperadas</p>
                        <p className="text-xl font-bold text-slate-700">
                          {detailedReportData.summary?.total_expected_formatted || "0h 00min"}
                        </p>
                      </div>
                      <div className={`p-4 rounded-lg border ${
                        (detailedReportData.summary?.balance_minutes || 0) >= 0 
                          ? "bg-green-50 border-green-200" 
                          : "bg-red-50 border-red-200"
                      }`}>
                        <p className={`text-xs font-medium ${
                          (detailedReportData.summary?.balance_minutes || 0) >= 0 
                            ? "text-green-600" 
                            : "text-red-600"
                        }`}>Saldo</p>
                        <p className={`text-xl font-bold ${
                          (detailedReportData.summary?.balance_minutes || 0) >= 0 
                            ? "text-green-700" 
                            : "text-red-700"
                        }`}>
                          {detailedReportData.summary?.balance_formatted || "0h 00min"}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-xs text-purple-600 font-medium">Dias</p>
                        <p className="text-xl font-bold text-purple-700">
                          {detailedReportData.summary?.days_worked || 0}/{detailedReportData.summary?.total_days || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Botão de PDF */}
                    <div className="flex justify-end">
                      <Button
                        onClick={generateDetailedPDF}
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                        data-testid="download-pdf-btn"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        Baixar PDF
                      </Button>
                    </div>
                    
                    {/* Tabela de registros */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="detailed-report-table">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-3 font-medium text-slate-700">Data</th>
                            <th className="text-left p-3 font-medium text-slate-700">Entrada</th>
                            <th className="text-left p-3 font-medium text-slate-700">Saída</th>
                            <th className="text-left p-3 font-medium text-slate-700">Entrada</th>
                            <th className="text-left p-3 font-medium text-slate-700">Saída</th>
                            <th className="text-left p-3 font-medium text-slate-700">Trabalhado</th>
                            <th className="text-left p-3 font-medium text-slate-700">Esperado</th>
                            <th className="text-left p-3 font-medium text-slate-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detailedReportData.daily_records || []).map((day, idx) => (
                            <tr key={idx} className={`border-b ${
                              day.status === "feriado" ? "bg-yellow-50" :
                              day.status === "folga" ? "bg-slate-50" :
                              day.status === "falta" ? "bg-red-50" :
                              ""
                            }`}>
                              <td className="p-3">
                                <div className="font-medium">{day.date_formatted || day.date}</div>
                                <div className="text-xs text-slate-500">{day.weekday}</div>
                              </td>
                              <td className="p-3 font-mono text-xs">
                                {day.entries?.[0] || "-"}
                              </td>
                              <td className="p-3 font-mono text-xs">
                                {day.exits?.[0] || "-"}
                              </td>
                              <td className="p-3 font-mono text-xs">
                                {day.entries?.[1] || "-"}
                              </td>
                              <td className="p-3 font-mono text-xs">
                                {day.exits?.[1] || "-"}
                              </td>
                              <td className="p-3 font-medium">
                                {day.worked_formatted || "-"}
                              </td>
                              <td className="p-3 text-slate-600">
                                {day.expected_formatted || "-"}
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  day.status === "completo" ? "bg-green-100 text-green-700" :
                                  day.status === "incompleto" ? "bg-orange-100 text-orange-700" :
                                  day.status === "feriado" ? "bg-yellow-100 text-yellow-700" :
                                  day.status === "folga" ? "bg-slate-100 text-slate-700" :
                                  day.status === "falta" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-600"
                                }`}>
                                  {day.status || "-"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// Main Employee Dashboard
export default function EmployeeDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: "Registrar Ponto", href: "/dashboard", icon: Clock },
    { name: "Meus Registros", href: "/dashboard/records", icon: FileText },
    { name: "Saldo de Horas", href: "/dashboard/balance", icon: TrendingUp },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (href) => {
    if (href === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#137082] transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <img 
              src="/logo-ciaponto-color.png" 
              alt="CiaPonto" 
              className="h-8 w-auto brightness-0 invert"
            />
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive(item.href)
                  ? "bg-[#1CA1BA] text-white"
                  : "text-slate-400 hover:text-white hover:bg-[#17889E]"
              }`}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="font-medium text-white">{user?.name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400">Funcionário</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-[#17889E]"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
          
          {/* Powered by CiaPonto */}
          <div className="mt-3 pt-3 border-t border-slate-700 text-center">
            <a 
              href="https://www.control-isp.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-cyan-400 transition-colors"
            >
              Powered by <span className="font-semibold">CiaPonto</span>
            </a>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 h-16">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-600"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                    <span className="font-medium text-ciaponto-cyan">{user?.name?.[0]}</span>
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<ClockInOut />} />
            <Route path="records" element={<MyRecords />} />
            <Route path="balance" element={<HoursBalance />} />
          </Routes>
        </main>
        
        {/* PWA Install Prompt */}
        <PWAInstallPrompt />
      </div>
    </div>
  );
}
